import React, { useEffect, useMemo, useState } from 'react'
import { IRequestedItem, IRequestedItemAllocation, ItemsRequestModel } from '.'
import WarehousesApp, { IWarehouseModel } from '../warehouses/WarehousesApp'
import SectionsApp, { ISectionModel } from '../sections/SectionsApp'
import { useDispatch } from 'react-redux'
import { handleRequestError } from '../../services/api'
import { Avatar, Button, Skeleton } from '@mui/material'
import GenerateIcon from '../../components/GenerateIcon'
import NoThumb from "../../assets/svg/no-thumb.svg"
import LocationsApp, { ILocationModel } from '../locations/LocationsApp'
import Thumbnail from '../../components/Thumbnail'
import { handleCloseElementQuickPreview, handleOpenElementQuickPreview } from '../admin/adminPagesSlice'
import { addNewSavedWarehouse, returnSavedSingleWarehouse } from '../warehouses/warehousesSlice'
import { RootState } from '../../app/store'
import { useSelector } from 'react-redux'

interface IRequestedItemAllocationProps {
    allocation: IRequestedItemAllocation,
    removeAllocationForItem: (itemId: number, warehouseId: number, sectionId: number) => void,
    requestedItem: IRequestedItem,
    viewRequestPage?: boolean,
    showReport: (requestedItem: IRequestedItem, allocation: IRequestedItemAllocation) => void,
    request: ItemsRequestModel
}

export function RequestedItemAllocation(props: IRequestedItemAllocationProps){

    const dispatch = useDispatch()

    const { allocation, removeAllocationForItem, requestedItem, viewRequestPage, showReport, request } = props

    const savedWarehouses = useSelector((state: RootState) => state.warehouses.savedWarehouses)

    const [warehouse, setWarehouse] = useState<IWarehouseModel | null>(null)
    const [section, setSection] = useState<ISectionModel | null>(null)
    const [location, setLocation] = useState<ILocationModel | null>(null)

    const warehousesApp = useMemo(() => new WarehousesApp(), [])
    const sectionsApp = new SectionsApp()
    const locationsApp = new LocationsApp()

    /**
     * Load warehouse
     */

    const loadWarehouse = () => {

        if (warehouse){
            return;
        }

        if (!allocation.warehouseId){
            return;
        }

        warehousesApp.singleInstanceDetails(allocation.warehouseId)
            .then(response => {
                setWarehouse(response.data)

                // Save warehouse details in the state
                dispatch(addNewSavedWarehouse(response.data))
            })
            .catch(error => {
                handleRequestError(error, dispatch)
            })
    }

    // useEffect(() => {
    //     loadWarehouse()
    // }, [warehouse])

    useEffect(() => {
        if (!allocation.warehouseId) return;
        if (allocation.warehouse_details){
            setWarehouse(allocation.warehouse_details)
            return;
        }

        // cache check
        const cached = savedWarehouses.find(w => w.id === allocation.warehouseId);
        if (cached) {
            setWarehouse(cached);
            return;
        }

        let cancelled = false;
        warehousesApp
            .singleInstanceDetails(allocation.warehouseId)
            .then(({ data }) => {
            if (cancelled) return;
            dispatch(addNewSavedWarehouse(data));
            setWarehouse(data);
            })
            .catch(error => handleRequestError(error, dispatch));

        return () => { cancelled = true; };
    }, [allocation.warehouseId, warehousesApp, dispatch]);

    /**
     * Load section
     */
    const loadSection = () => {
        if (section){
            return;
        }

        if (allocation.section_details){
            setSection(allocation.section_details)
            return;
        }

        if (!allocation.sectionId){
            return;
        }

        sectionsApp.singleInstanceDetails(allocation.sectionId)
            .then(response => {
                setSection(response.data)
            })
            .catch(error => {
                handleRequestError(error, dispatch)
            })
    }

    useEffect(() => {
        loadSection()
    }, [section])

    /**
     * Load location
     */
    const loadLocation = () => {
        if (location){
            return;
        }

        if (allocation.location_details){
            setLocation(allocation.location_details)
            return;
        }

        if (!allocation.locationId){
            return;
        }

        locationsApp.singleInstanceDetails(allocation.locationId)
            .then(response => {
                setLocation(response.data)
            })
            .catch(error => {
                handleRequestError(error, dispatch)
            })
    }

    useEffect(() => {
        loadLocation()
    }, [location])


    let showReportButton: boolean = false
    let disableReportButton: boolean = false

    if (request?.status === "fulfilled"){
        showReportButton = true
    }

    if (request?.status === "returned"){
        showReportButton = true
    }

    if (requestedItem.quantity === requestedItem.missing){
        disableReportButton = true
    }

    if (request.status === "closed"){
        showReportButton = false
    }

    /**
     * Render action buttons
     */
    const renderActionButtons = () => {

        const renderClearButton = () => <Button disabled={requestedItem.item.is_container ? true : false} className="general-button admin-button light-color" color="error" onClick={() => removeAllocationForItem(requestedItem.item_id, allocation.warehouseId, allocation.sectionId)}>Clear</Button>
        
        if (viewRequestPage){

            if (request && request.status === "pending"){
                return renderClearButton()
            }

            if (showReportButton){
                return <Button disabled={disableReportButton} className="general-button admin-button" color="error" onClick={() => showReport(requestedItem, allocation)}>Report</Button>
            }

            return <></>

        }else{
            return renderClearButton()
        }

        return <></>
    }

    let thumbnail = NoThumb

    if (warehouse && warehouse.thumbnail){
        thumbnail = warehouse.thumbnail
    }else{
        if (section && section.thumbnail){
            thumbnail = section.thumbnail
        }
    }

    if (location && location.thumbnail){
        thumbnail = location.thumbnail
    }

    return(
        <div className="single-requested-item-allocation">
            <div className="content-of-single-requested-item-allocation">

                <div className="fields-of-single-requested-item-allocation">
                    <div className="content-of-fields-of-single-requested-item-allocation">

                        <div className="field-of-single-requested-item-allocation thumbnail">
                            <div className="content-of-field-of-single-requested-item-allocation">
                                {/* <Avatar src={warehouse ? warehouse.thumbnail ? warehouse.thumbnail : NoThumb : NoThumb}>
                                    <GenerateIcon icon="image" />
                                </Avatar> */}
                                <Thumbnail src={thumbnail} />
                            </div>
                        </div>

                        {!location ? (
                            <>
                                <div className="field-of-single-requested-item-allocation text" style={{ maxWidth: 200, minWidth: 200 }}>
                                    <div className="content-of-field-of-single-requested-item-allocation">
                                        <h4>Warehouse</h4>
                                        <h3>
                                            {warehouse ? warehouse.title : <Skeleton variant="text" width={100} />}
                                        </h3>
                                    </div>
                                </div>

                                <div className="field-of-single-requested-item-allocation text" style={{ maxWidth: 200, minWidth: 200 }}>
                                    <div className="content-of-field-of-single-requested-item-allocation">
                                        <h4>Section</h4>
                                        <h3>{section ? section.title : <Skeleton variant="text" width={100} />}</h3>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="field-of-single-requested-item-allocation text" style={{ maxWidth: 200, minWidth: 200 }}>
                                <div className="content-of-field-of-single-requested-item-allocation">
                                    <h4>Location</h4>
                                    <h3>{location ? location.title : <Skeleton variant="text" width={100} />}</h3>
                                </div>
                            </div>
                        )}

                        <div className="field-of-single-requested-item-allocation text" style={{ maxWidth: 100, minWidth: 100 }}>
                            <div className="content-of-field-of-single-requested-item-allocation">
                                <h4>Requested Qnt.</h4>
                                <h3>{allocation.quantity}</h3>
                             </div>
                        </div>

                        {/* <div className="field-of-single-requested-item-allocation text error" style={{ maxWidth: 100, minWidth: 100 }}>
                            <div className="content-of-field-of-single-requested-item-allocation">
                                <h4>Missing Qnt.</h4>
                                <h3>{allocation.missing_qnt ? allocation.missing_qnt : 0}</h3>
                            </div>
                        </div>

                        <div className="field-of-single-requested-item-allocation text error" style={{ maxWidth: 100, minWidth: 100 }}>
                            <div className="content-of-field-of-single-requested-item-allocation">
                                <h4>Damaged Qnt.</h4>
                                <h3>{allocation.damaged_qnt ? allocation.damaged_qnt : 0}</h3>
                            </div>
                        </div> */}

                    </div>
                </div>

                {/* {!viewRequestPage ? (
                    <div className="action-buttons-of-fields-of-single-requested-item-allocation">
                        <div className="content-of-action-buttons-of-fields-of-single-requested-item-allocation">

                            <Button disabled={requestedItem.item.is_container ? true : false} className="general-button admin-button light-color" color="error" onClick={() => removeAllocationForItem(requestedItem.item_id, allocation.warehouseId, allocation.sectionId)}>Clear</Button>

                        </div>
                    </div>
                ) : (
                    <div className="action-buttons-of-fields-of-single-requested-item-allocation">
                        <div className="content-of-action-buttons-of-fields-of-single-requested-item-allocation">

                            {showReportButton && <Button disabled={disableReportButton} className="general-button admin-button" color="error" onClick={() => showReport(requestedItem, allocation)}>Report</Button>}

                        </div>
                    </div>
                )}        */}

                    <div className="action-buttons-of-fields-of-single-requested-item-allocation">
                        <div className="content-of-action-buttons-of-fields-of-single-requested-item-allocation">
                            {renderActionButtons()}
                        </div>
                    </div>         

            </div>
        </div>
    )
}

interface IRequestedItemAllocationsProps {
    requestedItem: IRequestedItem,
    removeAllocationForItem: (itemId: number, warehouseId: number, sectionId: number) => void,
    viewRequestPage?: boolean,
    showReport: (requestedItem: IRequestedItem, allocation: IRequestedItemAllocation) => void,
    request: ItemsRequestModel,
}

export default function RequestedItemAllocations(props: IRequestedItemAllocationsProps) {

    const { requestedItem, removeAllocationForItem, viewRequestPage, showReport, request } = props

    if (requestedItem.allocations.length === 0){
        return <></>
    }

    return(
        <div className="requested-item-allocations-container">
            <div className="content-of-requested-item-allocations-container">
                <h3 className="allocations-title">Allocations:</h3>
                {requestedItem.allocations.map((allocation, index) => <RequestedItemAllocation request={request} showReport={showReport} viewRequestPage={viewRequestPage} requestedItem={requestedItem} allocation={allocation} key={`${allocation.warehouseId}-${allocation.sectionId}`} removeAllocationForItem={removeAllocationForItem} />)}
            </div>
        </div>
    )
}